import React, { useCallback, useMemo } from 'react';
import { QMenuButton, QMenuItemType } from '../../QAtoms';
import { QBox } from '../';
import { SectionTypes } from '../../types/domains/SectionType';

export type TableOfContentsProps<T> = {
  sections?: SectionTypes<T>;
  editorName?: string;
};

export const TableOfContents = <T,>({
  sections,
  editorName,
}: TableOfContentsProps<T>): React.ReactElement | null => {
  const sortedSections = useMemo(
    () =>
      sections ? [...sections].sort((a, b) => a.position - b.position) : [],
    [sections],
  );

  const handleItemClick = useCallback((item: QMenuItemType) => {
    const position = parseInt(item.id) - 1;
    const sectionsHeadings = document.querySelectorAll(
      `#${editorName} h1.section-heading`,
    );
    if (sectionsHeadings.length > 0 && position <= sectionsHeadings.length) {
      sectionsHeadings[position].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const TOCItems: QMenuItemType[] = useMemo(
    () =>
      sortedSections.map((section, idx) => ({
        id: section.position.toString(),
        label: `${section.position}. ${section.title}`,
        'data-cy': `toc-element-${idx + 1}`,
        'data-testid': `toc-element-${idx + 1}`,
      })),
    [sortedSections],
  );

  if (sortedSections.length === 0) {
    return null;
  }

  return (
    <QBox
      px={2}
      py={4}
      height="fit-content"
      id="documentPageTocControl"
      data-cy="documentPageTocControl"
      data-testid="documentPageTocControl"
    >
      <QMenuButton
        items={TOCItems}
        onItemClick={handleItemClick}
        variant="icon"
        buttonLabel=""
        aria-label="toc-button"
        iconName="Menu"
        data-testid="toc-button"
        data-cy="toc-button"
      />
    </QBox>
  );
};
